/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Icon, Tag, Text, VFlow } from 'bold-ui'
import { orange } from 'bold-ui/lib/styles/colors'
import { usePrincipiosAtivosAlergiasQuery } from 'graphql/hooks.generated'
import { ReceitaMedicamento, TipoReceitaEnum, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { useMemo } from 'react'
import { tipoReceitaRecord } from 'types/enums'
import { formatDate } from 'util/date/formatDate'
import { periodoTratamento } from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/components/PeriodoTratamentoSelectField'

interface MedicamentosEmUsoCardProps {
  prontuarioId: ID
  receita: ReceitaMedicamento
}

export function MedicamentoEmUsoRow(props: Readonly<MedicamentosEmUsoCardProps>) {
  const { prontuarioId, receita } = props

  const {
    data: { principiosAtivosAlergias },
  } = usePrincipiosAtivosAlergiasQuery({
    variables: { prontuarioId: prontuarioId },
  })

  const isAlergico = useMemo(() => {
    const principioId = receita?.medicamento?.medicamentoCatmat?.principioAtivo?.id

    return principiosAtivosAlergias?.some((principio) => principio.id === principioId)
  }, [receita, principiosAtivosAlergias])

  const isVersaoAnterior = receita.atendProf?.versaoAnterior
  const hasEndDate = receita.medidaTempoTratamento !== UnidadeMedidaTempoEnum.INDETERMINADO && receita.dtFimTratamento

  const dataFim =
    !isVersaoAnterior && hasEndDate
      ? formatDate(receita.dtFimTratamento!)
      : periodoTratamento[receita.medidaTempoTratamento]

  return (
    <HFlow
      justifyContent='space-between'
      alignItems='center'
      style={css`
        padding: 0.5rem;
      `}
    >
      <VFlow vSpacing={0.25}>
        <HFlow hSpacing={0.5}>
          <Heading level={4}>
            {`${receita.medicamento.principioAtivo} ${receita.medicamento.concentracao ?? ''}`}
          </Heading>
          {receita.usoContinuo && <Tag>Uso contínuo</Tag>}
          {receita.tipoReceita !== TipoReceitaEnum.COMUM && (
            <Tag type='info'>{tipoReceitaRecord[receita.tipoReceita]}</Tag>
          )}
        </HFlow>
        <Text>{`${receita.posologia} (${receita.aplicacaoMedicamento.nome})`}</Text>
        {isAlergico && (
          <HFlow hSpacing={0.5}>
            <Icon color={orange.c40} icon='exclamationTriangleOutline' size={1} />
            <Text color='alert'>Alergia ao princípio ativo</Text>
          </HFlow>
        )}
      </VFlow>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon icon='calendarFilled' fill='primary' size={1} />
        <VFlow vSpacing={0.2}>
          <Text>
            De <Text fontWeight='bold'>{formatDate(receita.dtInicioTratamento)}</Text>
          </Text>
          <Text>
            até <Text fontWeight='bold'>{dataFim}</Text>
          </Text>
        </VFlow>
      </HFlow>
    </HFlow>
  )
}
